import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Settings2, BarChart3, ChevronRight, Lock, Wallet, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import MonthlySalesDashboard from "@/pages/owner/MonthlySalesDashboard";
import FinanceDashboard from "@/pages/owner/FinanceDashboard";
import VaultDashboard from "@/pages/owner/VaultDashboard";

export default function OwnerRoot() {
  const [view, setView] = useState("choose");
  const [pending, setPending] = useState(null);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  if (view === "pos") return <OwnerDashboard onHome={() => setView("choose")} />;
  if (view === "sales") return <MonthlySalesDashboard onHome={() => setView("choose")} />;
  if (view === "finance") return <FinanceDashboard onHome={() => setView("choose")} />;
  if (view === "vault") return <VaultDashboard onHome={() => setView("choose")} />;

  const cards = [
    { key: "pos", testid: "choose-pos", icon: Settings2, title: "Dashboard Pengaturan POS", desc: "Kelola katalog, staf, cabang, diskon, absensi, member, game & voucher." },
    { key: "sales", testid: "choose-sales", icon: BarChart3, title: "Dashboard Transaksi Penjualan Bulanan", desc: "Ringkasan omset & transaksi per cabang dengan faktor tampilan & cetak PDF." },
    { key: "finance", testid: "choose-finance", icon: Wallet, title: "Keuangan", desc: "Catat transaksi keuangan, arus kas, laba rugi internal, aset & laporan bulanan (PDF)." },
    { key: "vault", testid: "choose-vault", icon: KeyRound, title: "Pengingat & Password", desc: "Simpan username & password aplikasi, plus pengingat tagihan/iuran beserta foto struk." },
  ];

  const CARD_TITLES = { pos: "Dashboard Pengaturan POS", sales: "Dashboard Transaksi Penjualan Bulanan", finance: "Keuangan", vault: "Pengingat & Password" };

  const confirmEnter = async () => {
    if (!pwd.trim()) { toast.error("Masukkan password"); return; }
    setBusy(true);
    try { await api.post("/auth/verify-password", { password: pwd }); const t = pending; setPending(null); setPwd(""); setView(t); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-hotpot-grid pb-10" data-testid="owner-home">
      <TopBar showFactor />
      <div className="mx-auto max-w-4xl px-4 pt-10 sm:px-6">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl">Selamat Datang, Pemilik Restoran</h1>
        <p className="mb-8 text-sm text-muted-foreground">Pilih dashboard yang ingin Anda buka. Anda akan diminta memasukkan password sekali lagi.</p>
        <div className="grid gap-5 sm:grid-cols-2">
          {cards.map((c, i) => (
            <motion.button key={c.key} data-testid={c.testid} onClick={() => { setPending(c.key); setPwd(""); }}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
              className="card-surface group flex flex-col items-start gap-3 rounded-2xl p-6 text-left transition-all hover:-translate-y-1 hover:shadow-xl">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-crimson/10 text-crimson transition-colors group-hover:bg-crimson group-hover:text-white">
                <c.icon size={26} />
              </span>
              <div className="font-display text-lg font-extrabold text-neutral-900">{c.title}</div>
              <div className="text-sm text-muted-foreground">{c.desc}</div>
              <span className="mt-1 inline-flex items-center gap-1 text-sm font-bold text-crimson">Buka <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" /></span>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) { setPending(null); setPwd(""); } }}>
        <DialogContent className="glass border-neutral-200 text-neutral-900 sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Lock size={18} className="text-crimson" /> Konfirmasi Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Masukkan password Anda untuk membuka <b className="text-neutral-900">{CARD_TITLES[pending] || "dashboard"}</b>.</p>
            <Input data-testid="owner-gate-password" type="password" autoFocus value={pwd} onChange={(e) => setPwd(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && confirmEnter()} placeholder="Password" className="bg-neutral-100 border-neutral-200 text-neutral-900" />
            <Button data-testid="owner-gate-submit" onClick={confirmEnter} disabled={busy} className="w-full bg-crimson font-bold hover:bg-red-500">{busy ? "Memeriksa…" : "Buka Dashboard"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
